/**
 * Dynamic favicon hook - renders the module's Lucide icon as the primary
 * favicon with a colored background and a rainbow gradient border matching
 * the PanLuma logo colors.
 *
 * For the Home module (no icon/color), shows the original PanLuma favicon.
 * In local dev mode, adds an orange dot in the top-left corner to
 * distinguish local tabs from production.
 */

import { useEffect, useRef } from 'react';

const SIZE = 32;
const IS_LOCAL_DEV = import.meta.env.DEV;
const BORDER_RADIUS = 6;
const BORDER_WIDTH = 2;
const ICON_SIZE = 18; // Slightly smaller to leave room for the border
const ICON_OFFSET = (SIZE - ICON_SIZE) / 2;

// Local dev indicator: orange dot in top-left corner
const DEV_DOT_RADIUS = 4;
const DEV_DOT_CX = DEV_DOT_RADIUS + 1;
const DEV_DOT_CY = DEV_DOT_RADIUS + 1;
const DEV_DOT_COLOR = '#F97316'; // Tailwind orange-500

/**
 * Map from our icon config names to Lucide SVG path data.
 * Each entry is an array of [tagName, attributes] tuples — same format Lucide uses internally.
 * We only need the ~22 module-level icons, not every Lucide icon.
 */
type SvgElement = [string, Record<string, string>];

const MODULE_ICON_SVG: Record<string, SvgElement[]> = {
  // Marketing (Megaphone)
  Megaphone: [
    ['path', { d: 'M11 6a13 13 0 0 0 8.4-2.8A1 1 0 0 1 21 4v12a1 1 0 0 1-1.6.8A13 13 0 0 0 11 14H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z' }],
    ['path', { d: 'M6 14a12 12 0 0 0 2.4 7.2 2 2 0 0 0 3.2-2.4A8 8 0 0 1 10 14' }],
    ['path', { d: 'M8 6v8' }],
  ],
  // Home
  Home: [
    ['path', { d: 'M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8' }],
    ['path', { d: 'M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z' }],
  ],
  // Admin (Settings)
  Settings: [
    ['path', { d: 'M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915' }],
    ['circle', { cx: '12', cy: '12', r: '3' }],
  ],
  // Tasks (ClipboardList)
  Assignment: [
    ['rect', { width: '8', height: '4', x: '8', y: '2', rx: '1', ry: '1' }],
    ['path', { d: 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2' }],
    ['path', { d: 'M12 11h4' }],
    ['path', { d: 'M12 16h4' }],
    ['path', { d: 'M8 11h.01' }],
    ['path', { d: 'M8 16h.01' }],
  ],
  // Sales (Handshake)
  Handshake: [
    ['path', { d: 'm11 17 2 2a1 1 0 1 0 3-3' }],
    ['path', { d: 'm14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4' }],
    ['path', { d: 'm21 3 1 11h-2' }],
    ['path', { d: 'M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3' }],
    ['path', { d: 'M3 4h8' }],
  ],
  // Recruiting (Briefcase)
  Work: [
    ['path', { d: 'M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16' }],
    ['rect', { width: '20', height: '14', x: '2', y: '6', rx: '2' }],
  ],
  // People (Users)
  People: [
    ['path', { d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' }],
    ['path', { d: 'M16 3.128a4 4 0 0 1 0 7.744' }],
    ['path', { d: 'M22 21v-2a4 4 0 0 0-3-3.87' }],
    ['circle', { cx: '9', cy: '7', r: '4' }],
  ],
  // Accounting (Landmark)
  AccountBalance: [
    ['path', { d: 'M10 18v-7' }],
    ['path', { d: 'M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z' }],
    ['path', { d: 'M14 18v-7' }],
    ['path', { d: 'M18 18v-7' }],
    ['path', { d: 'M3 22h18' }],
    ['path', { d: 'M6 18v-7' }],
  ],
  // AI Agents (Bot)
  Bot: [
    ['path', { d: 'M12 8V4H8' }],
    ['rect', { width: '16', height: '12', x: '4', y: '8', rx: '2' }],
    ['path', { d: 'M2 14h2' }],
    ['path', { d: 'M20 14h2' }],
    ['path', { d: 'M15 13v2' }],
    ['path', { d: 'M9 13v2' }],
  ],
  // AI Chat (MessageSquare)
  Chat: [
    ['path', { d: 'M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z' }],
  ],
  // Messaging (MessagesSquare)
  Forum: [
    ['path', { d: 'M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z' }],
    ['path', { d: 'M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1' }],
  ],
  // Developer (Terminal)
  Api: [
    ['path', { d: 'M12 19h8' }],
    ['path', { d: 'm4 17 6-6-6-6' }],
  ],
  // Coding (Code)
  Code: [
    ['path', { d: 'm16 18 6-6-6-6' }],
    ['path', { d: 'm8 6-6 6 6 6' }],
  ],
  // Sysadmin (ShieldCheck)
  AdminPanelSettings: [
    ['path', { d: 'M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z' }],
    ['path', { d: 'm9 12 2 2 4-4' }],
  ],
  // Products (ShoppingBag)
  ShoppingBag: [
    ['path', { d: 'M16 10a4 4 0 0 1-8 0' }],
    ['path', { d: 'M3.103 6.034h17.794' }],
    ['path', { d: 'M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z' }],
  ],
  // Contacts (ContactRound)
  Contacts: [
    ['path', { d: 'M16 2v2' }],
    ['path', { d: 'M17.915 22a6 6 0 0 0-12 0' }],
    ['path', { d: 'M8 2v2' }],
    ['circle', { cx: '12', cy: '12', r: '4' }],
    ['rect', { x: '3', y: '4', width: '18', height: '18', rx: '2' }],
  ],
  // Support (Headset)
  SupportAgent: [
    ['path', { d: 'M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z' }],
    ['path', { d: 'M21 16v2a4 4 0 0 1-4 4h-5' }],
  ],
  // Website (Globe)
  Globe: [
    ['circle', { cx: '12', cy: '12', r: '10' }],
    ['path', { d: 'M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20' }],
    ['path', { d: 'M2 12h20' }],
  ],
  // Shipments (Ship)
  Ship: [
    ['path', { d: 'M12 10.189V14' }],
    ['path', { d: 'M12 2v3' }],
    ['path', { d: 'M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6' }],
    ['path', { d: 'M19.38 20A11.6 11.6 0 0 0 21 14l-8.188-3.639a2 2 0 0 0-1.624 0L3 14a11.6 11.6 0 0 0 2.81 7.76' }],
    ['path', { d: 'M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1s1.2 1 2.5 1c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1' }],
  ],
  // Files (FolderOpen)
  Files: [
    ['path', { d: 'm6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2' }],
  ],
  // Forms & BPM (FileText)
  Description: [
    ['path', { d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z' }],
    ['path', { d: 'M14 2v5a1 1 0 0 0 1 1h5' }],
    ['path', { d: 'M10 9H8' }],
    ['path', { d: 'M16 13H8' }],
    ['path', { d: 'M16 17H8' }],
  ],
  // Email (Mail)
  Email: [
    ['path', { d: 'm22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7' }],
    ['rect', { x: '2', y: '4', width: '20', height: '16', rx: '2' }],
  ],
  // Software Factory (Blocks)
  Blocks: [
    ['path', { d: 'M10 22V7a1 1 0 0 0-1-1H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5a1 1 0 0 0-1-1H2' }],
    ['rect', { x: '14', y: '2', width: '8', height: '8', rx: '1' }],
  ],
  // Inventory (Package)
  Package: [
    ['path', { d: 'M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z' }],
    ['path', { d: 'M12 22V12' }],
    ['polyline', { points: '3.29 7 12 12 20.71 7' }],
    ['path', { d: 'm7.5 4.27 9 5.15' }],
  ],
  // Procurement (HandCoins)
  HandCoins: [
    ['path', { d: 'M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17' }],
    ['path', { d: 'm7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9' }],
    ['path', { d: 'm2 16 6 6' }],
    ['circle', { cx: '16', cy: '9', r: '2.9' }],
    ['circle', { cx: '6', cy: '5', r: '3' }],
  ],
  // Integrations (Plug)
  Integrations: [
    ['path', { d: 'M12 22v-5' }],
    ['path', { d: 'M15 8V2' }],
    ['path', { d: 'M17 8a1 1 0 0 1 1 1v4a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1z' }],
    ['path', { d: 'M9 8V2' }],
  ],
  // Contracts (FileSignature → aliased to FilePenLine in lucide)
  FileSignature: [
    ['path', { d: 'm18.226 5.226-2.52-2.52A2.4 2.4 0 0 0 14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-.351' }],
    ['path', { d: 'M21.378 12.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z' }],
    ['path', { d: 'M8 18h1' }],
  ],
  // Billing (Receipt)
  Receipt: [
    ['path', { d: 'M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z' }],
    ['path', { d: 'M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8' }],
    ['path', { d: 'M12 17.5v-11' }],
  ],
};

/**
 * Convert an array of SVG element descriptors to SVG markup.
 */
function buildIconSvg(elements: SvgElement[]): string {
  return elements.map(([tag, attrs]) => {
    const attrStr = Object.entries(attrs)
      .map(([k, v]) => `${k}="${v}"`)
      .join(' ');
    return `<${tag} ${attrStr}/>`;
  }).join('');
}

/**
 * Build a complete favicon SVG: colored rounded-rect background with a
 * rainbow gradient border (matching PanLuma logo colors), white module icon
 * centered, and an optional orange dev dot in the top-left corner.
 */
function buildFaviconSvg(iconName: string, color: string): string | null {
  const iconElements = MODULE_ICON_SVG[iconName];
  if (!iconElements) return null;

  const iconSvgChildren = buildIconSvg(iconElements);
  const half = BORDER_WIDTH / 2;
  const innerR = BORDER_RADIUS - half;

  // Rainbow gradient border matching PanLuma logo: cyan → blue → purple → magenta → pink → orange → yellow → green
  const gradientDef = `<defs>
    <linearGradient id="rb" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#06B6D4"/>
      <stop offset="14%" stop-color="#3B82F6"/>
      <stop offset="28%" stop-color="#8B5CF6"/>
      <stop offset="42%" stop-color="#D946EF"/>
      <stop offset="56%" stop-color="#EC4899"/>
      <stop offset="70%" stop-color="#F97316"/>
      <stop offset="84%" stop-color="#EAB308"/>
      <stop offset="100%" stop-color="#22C55E"/>
    </linearGradient>
  </defs>`;

  // Local dev: orange dot in top-left corner
  const devDot = IS_LOCAL_DEV
    ? `<circle cx="${DEV_DOT_CX}" cy="${DEV_DOT_CY}" r="${DEV_DOT_RADIUS}" fill="${DEV_DOT_COLOR}"/>`
    : '';

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">
    ${gradientDef}
    <rect width="${SIZE}" height="${SIZE}" rx="${BORDER_RADIUS}" fill="${color}"/>
    <rect x="${half}" y="${half}" width="${SIZE - BORDER_WIDTH}" height="${SIZE - BORDER_WIDTH}" rx="${innerR}" fill="none" stroke="url(#rb)" stroke-width="${BORDER_WIDTH}"/>
    <g transform="translate(${ICON_OFFSET}, ${ICON_OFFSET})" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <svg viewBox="0 0 24 24" width="${ICON_SIZE}" height="${ICON_SIZE}">
        ${iconSvgChildren}
      </svg>
    </g>
    ${devDot}
  </svg>`;
}

// Cache for generated favicon data URLs
const faviconCache = new Map<string, string>();

function svgToDataUrl(svg: string): string {
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

function updateFaviconLinks(dataUrl: string) {
  const icon32 = document.querySelector<HTMLLinkElement>(
    'link[rel="icon"][sizes="32x32"]'
  );
  const icon16 = document.querySelector<HTMLLinkElement>(
    'link[rel="icon"][sizes="16x16"]'
  );

  if (icon32) {
    icon32.type = 'image/svg+xml';
    icon32.href = dataUrl;
  }
  if (icon16) {
    icon16.type = 'image/svg+xml';
    icon16.href = dataUrl;
  }
}

/**
 * Dynamically updates the favicon to show the module's icon on a colored background
 * with a rainbow gradient border. Falls back to the original PanLuma favicon for
 * modules without a known icon. Pass undefined iconName/color for the Home module.
 */
export function useDynamicFavicon(iconName: string | undefined, color: string | undefined) {
  const originalHrefsRef = useRef<{ icon32?: string; icon16?: string } | null>(null);

  useEffect(() => {
    // Store original favicon hrefs on first run
    if (originalHrefsRef.current === null) {
      const icon32 = document.querySelector<HTMLLinkElement>(
        'link[rel="icon"][sizes="32x32"]'
      );
      const icon16 = document.querySelector<HTMLLinkElement>(
        'link[rel="icon"][sizes="16x16"]'
      );
      originalHrefsRef.current = {
        icon32: icon32?.href,
        icon16: icon16?.href,
      };
    }

    // No icon = restore original (e.g., Home module)
    if (!iconName || !color) {
      if (originalHrefsRef.current) {
        const icon32 = document.querySelector<HTMLLinkElement>(
          'link[rel="icon"][sizes="32x32"]'
        );
        const icon16 = document.querySelector<HTMLLinkElement>(
          'link[rel="icon"][sizes="16x16"]'
        );
        if (icon32 && originalHrefsRef.current.icon32) icon32.href = originalHrefsRef.current.icon32;
        if (icon16 && originalHrefsRef.current.icon16) icon16.href = originalHrefsRef.current.icon16;
      }
      return;
    }

    // Cache key
    const cacheKey = IS_LOCAL_DEV ? `dev:${iconName}:${color}` : `${iconName}:${color}`;

    // Check cache
    const cached = faviconCache.get(cacheKey);
    if (cached) {
      updateFaviconLinks(cached);
      return;
    }

    // Build SVG and set favicon
    const svg = buildFaviconSvg(iconName, color);
    if (svg) {
      const dataUrl = svgToDataUrl(svg);
      faviconCache.set(cacheKey, dataUrl);
      updateFaviconLinks(dataUrl);
    }
  }, [iconName, color]);
}
